import { useEffect, useRef, useState } from "react";
import { getProdutividade } from "../../api/produtividade.api";

const PERIODO_OPCOES = [
  { valor: "atual", label: "Sprint atual" },
  { valor: 4,  label: "4 sprints" },
  { valor: 8,  label: "8 sprints" },
  { valor: 12, label: "12 sprints" },
  { valor: 24, label: "24 sprints" },
];

const SIZE_OPTS  = ["P", "M", "G", "XG", "sem_size"];
const SIZE_LABEL = { P: "Pequeno", M: "Médio", G: "Grande", XG: "Extra Grande", sem_size: "Sem Size" };
const SIZE_COLOR = { P: "#4ade80", M: "#60a5fa", G: "#fbbf24", XG: "#f87171", sem_size: "#94a3b8" };

const DEV_AVATAR_MAP = [
  { match: "guilherme", bg: "#712B13", color: "#F5C4B3" },
  { match: "paulo",     bg: "#0C447C", color: "#B5D4F4" },
  { match: "marcelo",   bg: "#085041", color: "#9FE1CB" },
  { match: "rudi",      bg: "#3C3489", color: "#CECBF6" },
  { match: "nelson",    bg: "#1C5040", color: "#A0DFC4" },
];
function devAv(nome) {
  if (!nome) return { bg: "#2C2C2A", color: "#B4B2A9" };
  const l = nome.toLowerCase();
  for (const m of DEV_AVATAR_MAP) if (l.includes(m.match)) return m;
  return { bg: "#2C2C2A", color: "#B4B2A9" };
}
function devInitials(nome) {
  if (!nome) return "?";
  const p = nome.trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : p[0][0].toUpperCase();
}

function fmtHoras(h) {
  if (h == null) return "—";
  return `${Number(h).toFixed(1)}h`;
}

/* pct < 0 → dev mais rápido que a equipe (menos horas) → verde
   pct > 0 → dev mais lento que a equipe (mais horas) → vermelho */
function pctColor(pct) {
  if (pct == null) return "#475569";
  if (pct < -5) return "#4ade80";
  if (pct > 5)  return "#f87171";
  return "#94a3b8";
}

function PctBadge({ pct }) {
  if (pct == null) return <span style={{ fontSize: 10, color: "#334155" }}>—</span>;
  const cor = pctColor(pct);
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, padding: "1px 7px", borderRadius: 99,
      color: cor, background: `${cor}1a`, border: `1px solid ${cor}40`, whiteSpace: "nowrap",
    }}>
      {pct > 0 ? "+" : ""}{pct}%
    </span>
  );
}

/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
export default function Produtividade() {
  const [data,    setData]    = useState(null);
  const [sprints, setSprints] = useState(8);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);
  const loadRef = useRef(null);

  async function load(qtd = sprints) {
    setLoading(true);
    setError(false);
    try {
      const res = await getProdutividade(qtd);
      setData(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  loadRef.current = () => load(sprints);

  useEffect(() => { load(sprints); }, [sprints]); // eslint-disable-line

  if (loading) return (
    <div style={page}>
      <div style={{ color: "#475569", textAlign: "center", paddingTop: 60, fontSize: 13 }}>
        Calculando produtividade dos programadores…
      </div>
    </div>
  );

  if (error) return (
    <div style={page}>
      <div style={{ color: "#f87171", textAlign: "center", paddingTop: 60, fontSize: 14 }}>
        Erro ao carregar dados.{" "}
        <button onClick={() => loadRef.current()} style={{ color: "#60a5fa", background: "none", border: "none", cursor: "pointer", fontSize: 14 }}>
          Tentar novamente
        </button>
      </div>
    </div>
  );

  const geral  = data?.geral  || {};
  const porSize = data?.por_size || {};
  const devs   = data?.devs || [];

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div>
          <h1 style={h1}>Produtividade dos Programadores</h1>
          <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>
            Horas de desenvolvimento apontadas até liberar o ticket · {data?.periodo
              ? (data.periodo.sprints === "atual"
                  ? `sprint atual (${data.periodo.versoes.join(", ")})`
                  : `${data.periodo.sprints} sprints (${data.periodo.versoes.join(", ")})`)
              : (sprints === "atual" ? "sprint atual" : `últimas ${sprints} sprints`)}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Período de análise
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              {PERIODO_OPCOES.map(opt => (
                <button
                  key={opt.valor}
                  onClick={() => setSprints(opt.valor)}
                  style={{
                    padding: "5px 12px", borderRadius: 6, cursor: "pointer",
                    fontSize: 11, fontWeight: 500, transition: "all 0.15s",
                    background: sprints === opt.valor ? "rgba(96,165,250,0.2)" : "rgba(255,255,255,0.04)",
                    border: sprints === opt.valor ? "1px solid rgba(96,165,250,0.4)" : "1px solid rgba(255,255,255,0.1)",
                    color: sprints === opt.valor ? "#60a5fa" : "rgba(255,255,255,0.4)",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => loadRef.current()} style={btnRefresh}>↻</button>
        </div>
      </header>

      {devs.length === 0 ? (
        <div style={{ color: "#334155", textAlign: "center", paddingTop: 40, fontSize: 13 }}>
          Nenhum apontamento de desenvolvimento encontrado no período selecionado.
        </div>
      ) : (
        <>
          {/* ── KPI gerais ── */}
          <div style={kpiRow}>
            <div style={{ ...kpiCard, borderColor: "rgba(96,165,250,0.2)" }}>
              <div style={kpiLabel}>Tickets Analisados</div>
              <div style={{ fontSize: 30, fontWeight: 900, color: "#60a5fa", lineHeight: 1 }}>
                {geral.tickets ?? 0}
              </div>
            </div>
            <div style={{ ...kpiCard, borderColor: "rgba(167,139,250,0.2)" }}>
              <div style={kpiLabel}>Média Geral de Horas</div>
              <div style={{ fontSize: 30, fontWeight: 900, color: "#a78bfa", lineHeight: 1 }}>
                {fmtHoras(geral.media_horas)}
              </div>
            </div>
            <div style={{ ...kpiCard, borderColor: "rgba(74,222,128,0.2)" }}>
              <div style={kpiLabel}>Mediana Geral de Horas</div>
              <div style={{ fontSize: 30, fontWeight: 900, color: "#4ade80", lineHeight: 1 }}>
                {fmtHoras(geral.mediana_horas)}
              </div>
            </div>
          </div>

          {/* ── Por Size (referência da equipe) ── */}
          <div style={glass}>
            <div style={sectionLabel}>Média da Equipe por Size</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
              {SIZE_OPTS.map(opt => {
                const s = porSize[opt] || { tickets: 0, media_horas: 0, mediana_horas: 0 };
                const cor = SIZE_COLOR[opt];
                return (
                  <div key={opt} style={{ ...sizeCard, borderColor: `${cor}30` }}>
                    <div style={{ color: cor, fontWeight: 800, fontSize: 12, marginBottom: 6 }}>
                      {SIZE_LABEL[opt]}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: "#e2e8f0", lineHeight: 1 }}>
                      {fmtHoras(s.media_horas)}
                    </div>
                    <div style={{ fontSize: 10, color: "#475569", marginTop: 4 }}>
                      mediana {fmtHoras(s.mediana_horas)} · {s.tickets} tk
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Por Desenvolvedor ── */}
          <div style={sectionLabel}>Por Desenvolvedor</div>
          <div style={devGrid}>
            {devs.map(dev => (
              <DevCard key={dev.desenvolvedor} dev={dev} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── DevCard ──────────────────────────────────────────────────────── */
function DevCard({ dev }) {
  const av = devAv(dev.desenvolvedor);
  const sizes = SIZE_OPTS.filter(opt => dev.por_size?.[opt]);

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: "50%",
          background: av.bg, color: av.color,
          fontSize: 13, fontWeight: 800, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {devInitials(dev.desenvolvedor)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {dev.desenvolvedor}
            </span>
            <PctBadge pct={dev.vs_equipe_pct} />
          </div>
          <div style={{ color: "#475569", fontSize: 10, marginTop: 2 }}>
            {dev.tickets} ticket{dev.tickets !== 1 ? "s" : ""} liberados
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ color: "#a78bfa", fontWeight: 800, fontSize: 20 }}>{fmtHoras(dev.media_horas)}</div>
          <div style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>média/ticket</div>
        </div>
      </div>

      {sizes.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, borderTop: "1px solid rgba(255,255,255,0.04)", paddingTop: 10 }}>
          {sizes.map(opt => {
            const s = dev.por_size[opt];
            const cor = SIZE_COLOR[opt];
            return (
              <div key={opt} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 30, color: cor, fontWeight: 800, fontSize: 11 }}>{opt === "sem_size" ? "—" : opt}</span>
                <span style={{ flex: 1, fontSize: 11, color: "#94a3b8" }}>
                  {fmtHoras(s.media_horas)} <span style={{ color: "#334155" }}>(equipe {fmtHoras(s.media_equipe_horas)})</span>
                </span>
                <span style={{ fontSize: 10, color: "#334155" }}>{s.tickets} tk</span>
                <PctBadge pct={s.vs_equipe_pct} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Estilos ──────────────────────────────────────────────────────── */

const page = { padding: "28px 32px", minHeight: "100vh", color: "#f1f5f9" };

const header = {
  display: "flex", alignItems: "flex-start",
  justifyContent: "space-between", marginBottom: 24, gap: 12, flexWrap: "wrap",
};

const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: 0, letterSpacing: "-0.01em" };

const btnRefresh = {
  background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.15)",
  color: "#60a5fa", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 16,
};

const kpiRow = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 10,
  marginBottom: 16,
};

const kpiCard = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  border: "1px solid",
  borderRadius: 12,
  padding: "14px 16px",
  minWidth: 100,
};

const kpiLabel = {
  fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)",
  textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6,
};

const glass = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
  marginBottom: 16,
};

const sectionLabel = {
  fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)",
  textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14,
};

const sizeCard = {
  background: "rgba(255,255,255,0.02)",
  border: "1px solid",
  borderRadius: 10,
  padding: "10px 12px",
};

const devGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
  gap: 12,
};

const card = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "14px 16px",
  display: "flex", flexDirection: "column", gap: 10,
};
